import { useCallback, useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import {
  GetPatientsBalanceResponse,
  GetPatientsBalanceResponse_PatientBalance,
} from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { useTranslation } from '@frontend/i18n';
import { useBulkCollectionsUIShallowStore } from '@frontend/payments-state';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { ModalControlTriggerProps, Table, TableInstance, Text } from '@frontend/design-system';
import { useGenerateColumns } from '../../../../hooks/use-generate-columns';
import { useBulkCollectionsFilterShallowStore } from '../../../../store/bulk-collections-filter-store';
import { BulkCreateStepId } from '../../BulkCreateModal/bulk-create-modal';
import { BULK_CREATE_STEPS } from '../../BulkCreateModal/bulk-create-steps-key-title';
import { CollectionColumn, generateColumns } from '../../common/generate-columns';
import { RowSelect } from '../../common/row-select';
import { searchByText } from '../../utils';
import { SelectAllCallout } from '../callouts/select-all-callout';
import { DesktopToolBar } from '../top-toolbar/desktop-toolbar';
import { FilterPanel } from './desktop-list/filter-panel';
import { useBulkCollectionsPagination } from './hooks/use-bulk-collections-pagination';

type DesktopListProps = {
  isLoading: boolean;
  collections?: GetPatientsBalanceResponse;
  triggerProps: ModalControlTriggerProps;
  selected: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  toggleInitialStep: (stepId: BulkCreateStepId) => void;
  onDismissBanner?: () => void;
  useNewApi?: boolean;
};
export const DesktopList = ({
  isLoading,
  collections,
  triggerProps,
  selected,
  onSelectionChange,
  toggleInitialStep,
  onDismissBanner,
  useNewApi,
}: DesktopListProps) => {
  const tableRef = useRef<TableInstance<GetPatientsBalanceResponse_PatientBalance>>(null);
  const { selectedLocationIds } = useAppScopeStore();
  const { t } = useTranslation('payments');
  const {
    currentPage,
    data,
    goNext,
    goPrev,
    hasNextPage,
    hasPreviousPage,
    isLoading: fetchingPage,
    pageSize,
    setPageSize,
    setOrderBy,
  } = useBulkCollectionsPagination({ enabled: useNewApi ?? false });

  const { shouldShowSelectAllCallout, dismissSelectAllCallout } = useBulkCollectionsUIShallowStore(
    'shouldShowSelectAllCallout',
    'dismissSelectAllCallout'
  );

  const { setSearchValue, ...filters } = useBulkCollectionsFilterShallowStore(
    'balanceType',
    'searchValue',
    'setSearchValue'
  );

  const original_columns = useGenerateColumns<CollectionColumn>(generateColumns, false, undefined, {
    hideLocationNameColumn: selectedLocationIds.length === 1,
  });

  const columns = useMemo(() => {
    if (useNewApi) {
      return [
        {
          id: 'id',
          Header: '',
          accessor: (invoice) => invoice,
          width: 50,
          disableSortBy: true,
          cellRenderer: (balance) => <RowSelect balance={balance} />,
        },
        ...original_columns,
      ];
    }
    return original_columns;
  }, [useNewApi]);

  const filteredCollections = useMemo(() => {
    let result = collections?.patientBalances ?? [];
    if (filters.searchValue) {
      result = searchByText(result, filters.searchValue);
    }

    if (filters.balanceType.length > 0) {
      result = result.filter((item) => filters.balanceType.includes(item.balanceType));
    }

    return result;
  }, [filters.balanceType, filters.searchValue, collections?.patientBalances]);

  useEffect(() => {
    if (!tableRef.current) return;

    const tableState = tableRef.current.state.selectedRowIds;
    const selectedSet = new Set(selected);

    // Select rows that are in props but not in table state
    selected.forEach((id) => {
      if (!tableState[id]) {
        tableRef.current?.toggleRowSelected(id, true);
      }
    });

    // Deselect rows that are in table state but not in props
    Object.keys(tableState).forEach((id) => {
      if (!selectedSet.has(id)) {
        tableRef.current?.toggleRowSelected(id, false);
      }
    });
  }, [selected]);

  const ref = useRef<HTMLDivElement>(null);

  const handleCalloutDismiss = () => {
    dismissSelectAllCallout();
    onDismissBanner?.();
  };

  const handleSort = useCallback((res: { id: string; value: 'asc' | 'desc' }[]) => {
    if (!res.length) {
      return;
    }
    const { id, value } = res[0] ?? {};
    if (id) {
      setOrderBy(`${id} ${value}`);
    }
  }, []);

  const paginationConfig = useMemo(() => {
    if (useNewApi) {
      // When using the new API, we want to use manual pagination so that we can control the data fetching.
      return {
        manualPaginationConfig: {
          page: currentPage,
          hasNext: !!hasNextPage,
          hasPrevious: hasPreviousPage,
          onNumRowsChange: (num: number) => {
            setPageSize(num);
          },
          defaultRowsPerPage: pageSize,
          rowsPerPageOptions: [20, 50, 100],
          handleChange: (prevOrNext: 'prev' | 'next') => {
            if (prevOrNext === 'prev') goPrev();
            if (prevOrNext === 'next') goNext();
          },
        },
      };
    }
    return {
      clientPaginationConfig: {
        defaultRowsPerPage: 20,
        rowsPerPageOptions: [20, 50, 100],
        showNumRowSelector: true,
      },
    };
  }, [useNewApi, currentPage, hasNextPage, hasPreviousPage, pageSize, setPageSize, goPrev, goNext]);

  const tableStyles = useMemo(() => {
    if (useNewApi) {
      // When using the new API, we don't show the checkboxes rendered by the table component.
      return undefined;
    }
    return css`
      // TODO:: replace this with a proper tooltip when DS supports it in table rows. UXP-907
      .table__row.disabled > .table__cell:first-child::after {
        content: '${t('Request can’t be created because no phone number is on file')}';
        position: absolute;
        top: 0%;
        left: 182px;
        transform: translateX(-50%);
        background: ${theme.colors.black};
        color: ${theme.colors.white};
        padding: ${theme.spacing(1)};
        border-radius: ${theme.borderRadius.small};
        width: 250px;
        text-align: center;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.2s;
        border: 0px;
      }
      .table__row.disabled > .table__cell:first-child::before {
        content: '';
        position: absolute;
        bottom: 40%;
        left: 47px;
        border-width: 5px;
        border-style: solid;
        border-color: transparent ${theme.colors.black} transparent transparent;
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.2s;
        top: auto;
        right: auto;
        width: auto;
        transform: none;
      }
      .table__row.disabled > .table__cell:first-child:hover::after,
      .table__row.disabled > .table__cell:first-child:hover::before {
        opacity: 1;
        z-index: 9;
      }
    `;
  }, [useNewApi]);

  const rowSelectionConfig = useMemo(() => {
    if (useNewApi) {
      // When using the new API, we don't want table component to manage the selection for rows.
      // it will be managed by <RowSelect /> component.
      return undefined;
    }
    return {
      isRowDisabled: ({ locationIds, phoneNumber }: { locationIds: string[]; phoneNumber?: string }) =>
        locationIds?.length > 1 || !phoneNumber,
      onSelectionToggle: (_selectedRows: any[], _data: any[], selectedIds: Record<string, boolean>) => {
        onSelectionChange(Object.keys(selectedIds));
        if (shouldShowSelectAllCallout) {
          handleCalloutDismiss();
        }
      },
      selectAllLabel: ({ selectedCount }: { selectedCount: number }) =>
        selectedCount > 0 ? (
          <Text color='primary' size='medium'>{`${selectedCount} selected`}</Text>
        ) : (
          <Text ref={ref} size='medium'>
            {t('Select All')}
          </Text>
        ),
      onSelectAllLabelClick: ({ selectedCount }: { selectedCount: number }) => {
        if (selectedCount > 0) {
          toggleInitialStep(BULK_CREATE_STEPS.VALIDATE_USERS.id);
          triggerProps.onClick?.();
        }
      },
    };
  }, [useNewApi, shouldShowSelectAllCallout]);

  return (
    <>
      {shouldShowSelectAllCallout && (
        <SelectAllCallout anchorRef={ref} showBanner={true} onDismiss={handleCalloutDismiss} />
      )}
      <Table
        isLoading={useNewApi ? fetchingPage : isLoading}
        keepPreviousDataWhileLoading={true}
        colConfig={columns}
        data={useNewApi ? data : filteredCollections}
        hasResponsiveColWidths
        instanceRef={tableRef}
        isSelectable={!useNewApi}
        uniqueRowId={(row) => row.patientId}
        onSortChange={useNewApi ? handleSort : undefined}
        manualFilters
        manualFiltersRender={(modalProps, setShowNotification) => (
          <FilterPanel modalProps={modalProps} setShowNotification={setShowNotification} />
        )}
        customToolbarRender={() => (
          <DesktopToolBar
            selected={selected}
            triggerProps={triggerProps}
            onSearchChange={setSearchValue}
            toggleInitialStep={toggleInitialStep}
            searchText={filters.searchValue}
            customSelectAllCheckbox={!!useNewApi}
          />
        )}
        rowSelectionConfig={rowSelectionConfig}
        isPaginated
        {...paginationConfig}
        tableStyle={tableStyles}
      />
    </>
  );
};
